import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import VehicleCard from '../components/VehicleCard';
import { useAnalytics } from '../hooks/useAnalytics';
import { useVehicleCache } from '../hooks/useVehicleCache';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  useAnalytics('inventory');
  const { getCachedVehicles, setCachedVehicles } = useVehicleCache();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialBrand = new URLSearchParams(window.location.search).get('brand');
  const [filters, setFilters] = useState({
    search: new URLSearchParams(window.location.search).get('search') || '',
    brands: initialBrand ? [initialBrand] : [],
    yearRange: [2000, 2030],
    priceRange: [0, 200000],
    maxMileage: 200000,
    transmission: [],
    fuelType: []
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    years: { min: 2000, max: 2030 },
    price: { min: 0, max: 200000 },
    mileage: { min: 0, max: 200000 },
    transmissions: [],
    fuel_types: []
  });
  const [displayCount, setDisplayCount] = useState(50); // Show 50 vehicles initially

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/filters/options`);
      
      // Filter out years from brands (only keep real brand names)
      const filteredBrands = response.data.brands.filter(brand => {
        // Exclude 4-digit numbers (years like 2015, 2016, etc.)
        if (brand && brand.length === 4 && !isNaN(brand)) {
          const year = parseInt(brand);
          return year < 1900 || year > 2030; // Keep only if not a year
        }
        return true; // Keep all non-numeric brands
      });
      
      setFilterOptions({
        ...response.data,
        brands: filteredBrands
      });
      
      // Update initial filter ranges based on actual data
      // Keep price max at 200,000 as requested (don't use API max)
      setFilters(prev => ({
        ...prev,
        yearRange: [response.data.years.min, response.data.years.max],
        priceRange: [response.data.price.min, 200000],
        maxMileage: response.data.mileage.max
      }));
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      
      // Always fetch all vehicles and filter client-side for better UX
      const response = await axios.get(`${API}/vehicles?limit=1000`);
      
      // Apply all filters client-side
      let filteredVehicles = response.data;
      
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredVehicles = filteredVehicles.filter(v => 
          v.brand?.toLowerCase().includes(searchLower) ||
          v.model?.toLowerCase().includes(searchLower) ||
          v.year?.toString().includes(searchLower) ||
          `${v.brand} ${v.model}`.toLowerCase().includes(searchLower)
        );
      }
      
      // Brand filter
      if (filters.brands.length > 0) {
        filteredVehicles = filteredVehicles.filter(v => 
          filters.brands.includes(v.brand)
        );
      }
      
      // Year range filter
      filteredVehicles = filteredVehicles.filter(v => 
        v.year >= filters.yearRange[0] && v.year <= filters.yearRange[1]
      );
      
      // Price range filter
      filteredVehicles = filteredVehicles.filter(v => 
        v.price >= filters.priceRange[0] && v.price <= filters.priceRange[1]
      );
      
      // Mileage filter
      filteredVehicles = filteredVehicles.filter(v => 
        v.mileage <= filters.maxMileage
      );
      
      // Transmission filter
      if (filters.transmission.length > 0) {
        filteredVehicles = filteredVehicles.filter(v => 
          filters.transmission.includes(v.transmission)
        );
      }
      
      // Fuel type filter
      if (filters.fuelType.length > 0) {
        filteredVehicles = filteredVehicles.filter(v => 
          filters.fuelType.includes(v.fuel_type)
        );
      }
      
      // Sort: Featured vehicles first, then by newest
      filteredVehicles.sort((a, b) => {
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      
      setVehicles(filteredVehicles);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-apply filters when any filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVehicles();
    }, 300); // Debounce 300ms
    
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleBrandToggle = (brand) => {
    setFilters(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand]
    }));
  };

  const handleTransmissionToggle = (transmission) => {
    setFilters(prev => ({
      ...prev,
      transmission: prev.transmission.includes(transmission)
        ? prev.transmission.filter(t => t !== transmission)
        : [...prev.transmission, transmission]
    }));
  };

  const handleFuelTypeToggle = (fuelType) => {
    setFilters(prev => ({
      ...prev,
      fuelType: prev.fuelType.includes(fuelType)
        ? prev.fuelType.filter(f => f !== fuelType)
        : [...prev.fuelType, fuelType]
    }));
  };

  const handleReset = () => {
    setFilters({
      search: '',
      brands: [],
      yearRange: [filterOptions.years.min, filterOptions.years.max],
      priceRange: [0, 200000],
      maxMileage: 200000,
      transmission: [],
      fuelType: []
    });
  };

  return (
    <>
      <SEOHead 
        title="Vehicle Inventory | AAA of Miami | Premium Cars for Sale"
        description={`Browse our inventory of ${vehicles.length}+ premium luxury vehicles. Find your perfect car with advanced filters by brand, year, price, and more. BMW, Mercedes, Porsche & exotic cars.`}
        type="website"
      />
      <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
        <div className="flex">
        {/* Sidebar Filters */}
        <aside 
          className={`fixed lg:sticky top-20 left-0 h-[calc(100vh-5rem)] overflow-y-auto transition-transform duration-300 z-40 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
          style={{ 
            width: '320px',
            background: 'var(--bg-primary)',
            borderRight: '1px solid var(--border-color)'
          }}
        >
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Playfair Display' }}>
                {t('filters.title')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarOpen(false)}
                className="lg:hidden"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Search */}
            <div>
              <Label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>{t('filters.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                <Input
                  type="text"
                  placeholder={t('hero.search')}
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                  data-testid="sidebar-search-input"
                />
              </div>
            </div>

            {/* Brands */}
            <div>
              <Label className="text-sm font-semibold mb-3 block" style={{ color: 'var(--text-primary)' }}>{t('filters.brand')}</Label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filterOptions.brands.map(brand => (
                  <div key={brand} className="flex items-center space-x-2">
                    <Checkbox
                      id={`brand-${brand}`}
                      checked={filters.brands.includes(brand)}
                      onCheckedChange={() => handleBrandToggle(brand)}
                      data-testid={`brand-${brand}`}
                    />
                    <label
                      htmlFor={`brand-${brand}`}
                      className="text-sm cursor-pointer flex-1"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {brand}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Year Range */}
            <div>
              <Label className="text-sm font-semibold mb-3 block" style={{ color: 'var(--text-primary)' }}>
                {t('filters.yearRange')}: {filters.yearRange[0]} - {filters.yearRange[1]}
              </Label>
              <Slider
                min={filterOptions.years.min}
                max={filterOptions.years.max}
                step={1}
                value={filters.yearRange}
                onValueChange={(value) => setFilters(prev => ({ ...prev, yearRange: value }))}
                className="mb-2"
                data-testid="year-range-slider"
              />
            </div>

            {/* Price Range */}
            <div>
              <Label className="text-sm font-semibold mb-3 block" style={{ color: 'var(--text-primary)' }}>
                {t('filters.priceRange')}: ${filters.priceRange[0].toLocaleString()} - ${filters.priceRange[1].toLocaleString()}
              </Label>
              <Slider
                min={filterOptions.price.min}
                max={200000}
                step={1000}
                value={filters.priceRange}
                onValueChange={(value) => setFilters(prev => ({ ...prev, priceRange: value }))}
                className="mb-2"
                data-testid="price-range-slider"
              />
            </div>

            {/* Max Mileage */}
            <div>
              <Label className="text-sm font-semibold mb-3 block" style={{ color: 'var(--text-primary)' }}>
                {t('filters.maxMileage')}: {filters.maxMileage.toLocaleString()} {t('filters.miles')}
              </Label>
              <Slider
                min={filterOptions.mileage.min}
                max={filterOptions.mileage.max}
                step={5000}
                value={[filters.maxMileage]}
                onValueChange={(value) => setFilters(prev => ({ ...prev, maxMileage: value[0] }))}
                className="mb-2"
                data-testid="mileage-slider"
              />
            </div>

            {/* Transmission */}
            <div>
              <Label className="text-sm font-semibold mb-3 block" style={{ color: 'var(--text-primary)' }}>{t('filters.transmission')}</Label>
              <div className="space-y-2">
                {filterOptions.transmissions.map(transmission => (
                  <div key={transmission} className="flex items-center space-x-2">
                    <Checkbox
                      id={`transmission-${transmission}`}
                      checked={filters.transmission.includes(transmission)}
                      onCheckedChange={() => handleTransmissionToggle(transmission)}
                      data-testid={`transmission-${transmission}`}
                    />
                    <label
                      htmlFor={`transmission-${transmission}`}
                      className="text-sm cursor-pointer"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {transmission}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Fuel Type */}
            <div>
              <Label className="text-sm font-semibold mb-3 block" style={{ color: 'var(--text-primary)' }}>{t('filters.fuelType')}</Label>
              <div className="space-y-2">
                {filterOptions.fuel_types.map(fuelType => (
                  <div key={fuelType} className="flex items-center space-x-2">
                    <Checkbox
                      id={`fuel-${fuelType}`}
                      checked={filters.fuelType.includes(fuelType)}
                      onCheckedChange={() => handleFuelTypeToggle(fuelType)}
                      data-testid={`fuel-${fuelType}`}
                    />
                    <label
                      htmlFor={`fuel-${fuelType}`}
                      className="text-sm cursor-pointer"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {fuelType}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              {/* Active filters count */}
              {(filters.brands.length > 0 || filters.transmission.length > 0 || filters.fuelType.length > 0 || filters.search) && (
                <div className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span className="font-medium" style={{ color: 'var(--accent-color)' }}>
                    {filters.brands.length + filters.transmission.length + filters.fuelType.length + (filters.search ? 1 : 0)}
                  </span> filtre aktif
                </div>
              )}
              <Button
                onClick={handleReset}
                variant="outline"
                className="w-full"
                data-testid="reset-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                {t('filters.resetAll')}
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Filter Toggle */}
        {!isSidebarOpen && (
          <Button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed bottom-6 left-6 z-50 lg:hidden rounded-full w-14 h-14 shadow-lg"
            data-testid="mobile-filter-toggle"
          >
            <SlidersHorizontal className="w-6 h-6" />
          </Button>
        )}

        {/* Main Content */}
        <div className="flex-1 lg:ml-0" style={{ marginLeft: isSidebarOpen ? '0' : '0' }}>
          <div className="max-w-7xl mx-auto px-6 py-12">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="inventory-title">
                Our Inventory
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                {loading ? t('common.loading') : `${vehicles.length} ${t('filters.vehiclesFound')}`}
              </p>
            </div>

            {/* Vehicle Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inventory-skeleton">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl overflow-hidden border"
                    style={{ borderColor: 'var(--border-color)', background: 'var(--bg-primary)' }}
                  >
                    <div className="skeleton h-64 w-full rounded-none" />
                    <div className="p-6 space-y-4">
                      <div className="skeleton h-6 w-2/3" />
                      <div className="flex justify-between">
                        <div className="skeleton h-8 w-24" />
                        <div className="skeleton h-6 w-16" />
                      </div>
                      <div className="flex justify-between">
                        <div className="skeleton h-4 w-28" />
                        <div className="skeleton h-4 w-20" />
                      </div>
                      <div className="skeleton h-10 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : vehicles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicles-grid">
                  {vehicles.slice(0, displayCount).map(vehicle => (
                    <VehicleCard key={vehicle.id} vehicle={vehicle} lang={lang} />
                  ))}
                </div>
                
                {displayCount < vehicles.length && (
                  <div className="flex justify-center mt-8">
                    <Button 
                      onClick={() => setDisplayCount(prev => prev + 50)}
                      className="px-8 py-3"
                      data-testid="load-more-btn"
                    >
                      {t('common.loadMore')} ({vehicles.length - displayCount} {t('filters.more')})
                    </Button>
                  </div>
                )}
                
                {displayCount >= vehicles.length && vehicles.length > 50 && (
                  <div className="text-center mt-8" style={{ color: 'var(--text-secondary)' }}>
                    {t('filters.allVehiclesShown')} ({vehicles.length} {t('filters.total')})
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>No vehicles found matching your criteria.</p>
                <Button onClick={handleReset} data-testid="reset-search-btn">
                  Reset Search
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default InventoryPage;
